import { useState, useEffect, useCallback } from 'react'
import {
  Building,
  Palette,
  Bell,
  Shield,
  User,
  Mail,
  Phone,
  MapPin,
  Save,
  Upload,
  Eye,
  EyeOff,
  Key,
  CreditCard,
  Globe,
  Settings as SettingsIcon,
  Monitor,
  Smartphone,
  Users,
  Database,
  Lock,
  RefreshCw,
  Check,
  X,
  Info,
  AlertTriangle,
  Trash2,
  Edit,
  AlertCircle,
  ExternalLink
} from 'lucide-react'
import { Select } from '../components/ui/Select'
import { partnerAdminApi, storageUtils, whitelabelApi } from '../services/api'
import type { PartnerProfile, PartnerBrandingConfig, NotificationSettings, MfaSetupResponse } from '../services/api'

// Componente de indicador para funcionalidades nao implementadas
function NotImplementedBadge({ tooltip }: { tooltip?: string }) {
  return (
    <span
      className="inline-flex items-center ml-2 px-1.5 py-0.5 rounded text-xs font-medium bg-red-100 text-red-700 cursor-help"
      title={tooltip || 'Funcionalidade ainda nao implementada no backend'}
    >
      <AlertCircle className="w-3 h-3 mr-1" />
      Em breve
    </span>
  )
}

// Valores padrao para NotificationSettings
const DEFAULT_NOTIFICATION_SETTINGS: NotificationSettings = {
  email_notifications: true,
  push_notifications: true,
  sms_notifications: false,
  notifications: {
    tax_deadlines: true,
    document_uploads: true,
    banking_updates: true,
    system_alerts: true,
    marketing: false
  },
  digest_frequency: 'daily',
  quiet_hours: {
    enabled: false,
    start: '22:00',
    end: '08:00'
  }
}

// Componente de Seguranca separado para melhor organizacao
function SecurityTab() {
  const [mfaStatus, setMfaStatus] = useState<{ mfa_enabled: boolean } | null>(null)
  const [sessionInfo, setSessionInfo] = useState<{
    currentSession: { ip: string; userAgent: string; loginTime: string }
    currentSessionId: string | null
    lastLogin: string | null
    otherSessions?: Array<{
      id: string
      ip: string | null
      userAgent: string | null
      createdAt: string
      lastSeen: string | null
    }>
  } | null>(null)
  const [revokingSessionId, setRevokingSessionId] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  // Form states para alterar senha
  const [showPasswordForm, setShowPasswordForm] = useState(false)
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showCurrentPassword, setShowCurrentPassword] = useState(false)
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [mfaSetupData, setMfaSetupData] = useState<MfaSetupResponse | null>(null)
  const [mfaCode, setMfaCode] = useState('')
  const [mfaDisableCode, setMfaDisableCode] = useState('')
  const [mfaLoading, setMfaLoading] = useState(false)
  const [terminatingSessions, setTerminatingSessions] = useState(false)

  // Carregar dados de seguranca
  const refreshSecurityData = useCallback(async () => {
    try {
      setLoading(true)
      const [mfa, session] = await Promise.all([
        partnerAdminApi.security.getMfaStatus().catch(() => ({ mfa_enabled: false })),
        partnerAdminApi.security.getSessions().catch(() => null)
      ])
      setMfaStatus(mfa)
      setSessionInfo(session)
    } catch (err) {
      console.error('Error fetching security data:', err)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    refreshSecurityData()
  }, [refreshSecurityData])

  // Handler para alterar senha
  const handleChangePassword = async () => {
    if (newPassword !== confirmPassword) {
      setError('As senhas nao coincidem')
      return
    }
    if (newPassword.length < 8) {
      setError('A nova senha deve ter no minimo 8 caracteres')
      return
    }

    try {
      setSaving(true)
      setError(null)
      await partnerAdminApi.security.changePassword(currentPassword, newPassword)
      setSuccess('Senha alterada com sucesso!')
      setShowPasswordForm(false)
      setCurrentPassword('')
      setNewPassword('')
      setConfirmPassword('')
      setTimeout(() => setSuccess(null), 3000)
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erro ao alterar senha')
    } finally {
      setSaving(false)
    }
  }

  const handleStartMfaSetup = async () => {
    try {
      setMfaLoading(true)
      setError(null)
      const setup = await partnerAdminApi.security.setupMfa()
      setMfaSetupData(setup)
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erro ao iniciar configuracao do 2FA')
    } finally {
      setMfaLoading(false)
    }
  }

  const handleConfirmMfa = async () => {
    if (!mfaCode) return
    try {
      setMfaLoading(true)
      setError(null)
      await partnerAdminApi.security.confirmMfa(mfaCode)
      setSuccess('2FA habilitado com sucesso!')
      setMfaSetupData(null)
      setMfaCode('')
      await refreshSecurityData()
      setTimeout(() => setSuccess(null), 3000)
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erro ao confirmar 2FA')
    } finally {
      setMfaLoading(false)
    }
  }

  const handleDisableMfa = async () => {
    if (!mfaDisableCode) return
    try {
      setMfaLoading(true)
      setError(null)
      await partnerAdminApi.security.disableMfa(mfaDisableCode)
      setSuccess('2FA desabilitado com sucesso!')
      setMfaDisableCode('')
      await refreshSecurityData()
      setTimeout(() => setSuccess(null), 3000)
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erro ao desabilitar 2FA')
    } finally {
      setMfaLoading(false)
    }
  }

  const handleTerminateOtherSessions = async () => {
    try {
      setTerminatingSessions(true)
      setError(null)
      const result = await partnerAdminApi.security.terminateOtherSessions()
      const user = storageUtils.getUser()
      const partner = storageUtils.getPartner()
      if (user && partner) {
        storageUtils.setAuthData(result.access_token, user, partner, result.refresh_token)
      }
      setSuccess('Outras sessoes encerradas com sucesso!')
      await refreshSecurityData()
      setTimeout(() => setSuccess(null), 3000)
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erro ao encerrar outras sessoes')
    } finally {
      setTerminatingSessions(false)
    }
  }

  const handleRevokeSession = async (sessionId: string) => {
    try {
      setRevokingSessionId(sessionId)
      setError(null)
      await partnerAdminApi.security.revokeSession(sessionId)
      setSuccess('Sessao encerrada com sucesso!')
      await refreshSecurityData()
      setTimeout(() => setSuccess(null), 3000)
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erro ao encerrar sessao')
    } finally {
      setRevokingSessionId(null)
    }
  }

  // Extrair nome do navegador do user agent
  const getBrowserName = (userAgent: string) => {
    if (userAgent.includes('Chrome')) return 'Google Chrome'
    if (userAgent.includes('Firefox')) return 'Mozilla Firefox'
    if (userAgent.includes('Safari')) return 'Safari'
    if (userAgent.includes('Edge')) return 'Microsoft Edge'
    return 'Navegador'
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-32">
        <RefreshCw className="w-6 h-6 animate-spin text-blue-600" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Alerts */}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 flex items-center gap-2">
          <AlertTriangle size={20} />
          {error}
          <button onClick={() => setError(null)} className="ml-auto">
            <X size={16} />
          </button>
        </div>
      )}
      {success && (
        <div className="p-4 bg-green-50 border border-green-200 rounded-lg text-green-700 flex items-center gap-2">
          <Check size={20} />
          {success}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Autenticacao */}
        <div className="space-y-4">
          <h3 className="text-lg font-medium text-gray-900">
            Autenticacao
          </h3>

          {/* Alterar Senha */}
          {!showPasswordForm ? (
            <button
              onClick={() => setShowPasswordForm(true)}
              className="w-full flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center gap-3">
                <Key className="w-5 h-5 text-blue-600" />
                <div className="text-left">
                  <p className="font-medium text-gray-900">Alterar Senha</p>
                  <p className="text-sm text-gray-500">Atualize sua senha de acesso</p>
                </div>
              </div>
              <Edit className="w-4 h-4 text-gray-400" />
            </button>
          ) : (
            <div className="p-4 border border-blue-200 rounded-lg bg-blue-50 space-y-4">
              <div className="flex items-center justify-between">
                <h4 className="font-medium text-gray-900">Alterar Senha</h4>
                <button onClick={() => setShowPasswordForm(false)} className="text-gray-400 hover:text-gray-600">
                  <X size={20} />
                </button>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Senha Atual</label>
                <div className="relative">
                  <input
                    type={showCurrentPassword ? 'text' : 'password'}
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"
                  >
                    {showCurrentPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nova Senha</label>
                <div className="relative">
                  <input
                    type={showNewPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"
                  >
                    {showNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-1">Minimo 8 caracteres</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Confirmar Nova Senha</label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <button
                onClick={handleChangePassword}
                disabled={saving || !currentPassword || !newPassword || !confirmPassword}
                className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                {saving ? 'Salvando...' : 'Salvar Nova Senha'}
              </button>
            </div>
          )}

          {/* 2FA Status */}
          <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
            <div className="flex items-center gap-3">
              <Shield className={`w-5 h-5 ${mfaStatus?.mfa_enabled ? 'text-green-600' : 'text-gray-400'}`} />
              <div>
                <p className="font-medium text-gray-900">Autenticacao 2FA</p>
                <p className="text-sm text-gray-500">
                  {mfaStatus?.mfa_enabled ? 'Habilitado' : 'Desabilitado'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {mfaStatus?.mfa_enabled ? (
                <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-700">
                  Ativo
                </span>
              ) : (
                <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-500">
                  Inativo
                </span>
              )}
            </div>
          </div>

          {!mfaStatus?.mfa_enabled ? (
            <div className="p-4 border border-gray-200 rounded-lg bg-gray-50">
              {!mfaSetupData ? (
                <button
                  onClick={handleStartMfaSetup}
                  disabled={mfaLoading}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {mfaLoading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Shield className="w-4 h-4" />}
                  {mfaLoading ? 'Gerando...' : 'Ativar 2FA'}
                </button>
              ) : (
                <div className="space-y-4">
                  <div className="text-sm text-gray-700">{mfaSetupData.instructions}</div>
                  <div className="flex flex-col items-center gap-3">
                    <img src={mfaSetupData.qr_code} alt="QR Code 2FA" className="w-40 h-40 border border-gray-200 rounded-lg bg-white" />
                    <div className="text-xs text-gray-500">
                      Chave manual: <span className="font-mono">{mfaSetupData.secret_key}</span>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Codigo do app</label>
                    <input
                      type="text"
                      value={mfaCode}
                      onChange={(e) => setMfaCode(e.target.value)}
                      placeholder="000000"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={handleConfirmMfa}
                      disabled={mfaLoading || !mfaCode}
                      className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {mfaLoading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                      Confirmar
                    </button>
                    <button
                      onClick={() => setMfaSetupData(null)}
                      disabled={mfaLoading}
                      className="flex-1 flex items-center justify-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <X className="w-4 h-4" />
                      Cancelar
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="p-4 border border-gray-200 rounded-lg bg-gray-50 space-y-3">
              <div className="text-sm text-gray-700">
                Para desativar, informe o codigo do seu app autenticador.
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={mfaDisableCode}
                  onChange={(e) => setMfaDisableCode(e.target.value)}
                  placeholder="000000"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <button
                  onClick={handleDisableMfa}
                  disabled={mfaLoading || !mfaDisableCode}
                  className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {mfaLoading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <X className="w-4 h-4" />}
                  Desativar
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Sessoes */}
        <div className="space-y-4">
          <h3 className="text-lg font-medium text-gray-900">
            Sessao Atual
          </h3>

          <div className="space-y-2">
            <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-center gap-3 mb-2">
                <Monitor className="w-5 h-5 text-green-600" />
                <div>
                  <p className="font-medium text-gray-900">
                    {sessionInfo?.currentSession ? getBrowserName(sessionInfo.currentSession.userAgent) : 'Navegador atual'}
                  </p>
                  <p className="text-sm text-green-600">Sessao ativa</p>
                </div>
              </div>
              {sessionInfo?.currentSession && (
                <div className="text-xs text-gray-500 mt-2 pl-8">
                  <p>IP: {sessionInfo.currentSession.ip}</p>
                </div>
              )}
            </div>

            {sessionInfo?.lastLogin && (
              <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
                <p className="text-sm text-gray-600">
                  <span className="font-medium">Ultimo login:</span>{' '}
                  {new Date(sessionInfo.lastLogin).toLocaleString('pt-BR')}
                </p>
              </div>
            )}
          </div>

          {sessionInfo?.otherSessions && (
            <div className="space-y-2">
              <h4 className="text-sm font-medium text-gray-900">
                Outras sessoes ativas ({sessionInfo.otherSessions.length})
              </h4>
              {sessionInfo.otherSessions.length === 0 ? (
                <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-600">
                  Nenhuma outra sessao ativa.
                </div>
              ) : (
                sessionInfo.otherSessions.map((session) => (
                  <div key={session.id} className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg flex items-center justify-between gap-3">
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        {session.userAgent ? getBrowserName(session.userAgent) : 'Dispositivo'}
                      </p>
                      <p className="text-xs text-gray-600">
                        IP: {session.ip || 'Desconhecido'}
                      </p>
                      <p className="text-xs text-gray-600">
                        Visto por ultimo:{' '}
                        {session.lastSeen ? new Date(session.lastSeen).toLocaleString('pt-BR') : 'Desconhecido'}
                      </p>
                    </div>
                    <button
                      onClick={() => handleRevokeSession(session.id)}
                      disabled={revokingSessionId === session.id}
                      className="px-3 py-1.5 text-xs text-red-700 bg-red-100 hover:bg-red-200 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {revokingSessionId === session.id ? 'Encerrando...' : 'Encerrar'}
                    </button>
                  </div>
                ))
              )}
            </div>
          )}

          <div>
            <button
              onClick={handleTerminateOtherSessions}
              disabled={terminatingSessions || (sessionInfo?.otherSessions?.length === 0)}
              className="w-full px-4 py-2 text-sm text-gray-700 border border-gray-200 rounded-lg bg-gray-50 hover:bg-gray-100 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Lock className="w-4 h-4" />
              {terminatingSessions ? 'Encerrando...' : 'Encerrar outras sessoes'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

/**
 * Settings Page for Partner Admin
 *
 * Purpose: Allow partners to configure their profile, branding, and preferences
 * Features:
 * - Partner profile management (REAL - conectado ao backend)
 * - Branding customization -> Redireciona para Whitelabel
 * - Notification preferences (REAL - conectado ao backend)
 * - Security settings (EM BREVE - mock)
 * - Billing information (EM BREVE - mock)
 *
 * Data Isolation: Only shows settings for current partner
 */
export function PartnerSettings() {
  const [activeTab, setActiveTab] = useState<'profile' | 'branding' | 'notifications' | 'security' | 'billing'>('profile')
  const [profile, setProfile] = useState<PartnerProfile | null>(null)
  const [branding, setBranding] = useState<PartnerBrandingConfig | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  // Form states para edicao
  const [editName, setEditName] = useState('')
  const [editContactEmail, setEditContactEmail] = useState('')
  const [editContactPhone, setEditContactPhone] = useState('')

  // Endereco separado
  const [editCep, setEditCep] = useState('')
  const [editStreet, setEditStreet] = useState('')
  const [editNumber, setEditNumber] = useState('')
  const [editComplement, setEditComplement] = useState('')
  const [editNeighborhood, setEditNeighborhood] = useState('')
  const [editCity, setEditCity] = useState('')
  const [editState, setEditState] = useState('')
  const [loadingCep, setLoadingCep] = useState(false)

  // Notification settings - REAL data from backend
  const [notificationSettings, setNotificationSettings] = useState<NotificationSettings>(DEFAULT_NOTIFICATION_SETTINGS)
  const [notificationsLoading, setNotificationsLoading] = useState(false)

  // Funcao para buscar endereco pelo CEP
  const fetchAddressByCep = async (cep: string) => {
    const cleanCep = cep.replace(/\D/g, '')
    if (cleanCep.length !== 8) return

    setLoadingCep(true)
    try {
      const response = await fetch(`https://viacep.com.br/ws/${cleanCep}/json/`)
      const data = await response.json()

      if (!data.erro) {
        setEditStreet(data.logradouro || '')
        setEditNeighborhood(data.bairro || '')
        setEditCity(data.localidade || '')
        setEditState(data.uf || '')
      }
    } catch (err) {
      console.error('Erro ao buscar CEP:', err)
    } finally {
      setLoadingCep(false)
    }
  }

  // Parser de endereco completo para campos separados
  const parseAddress = (fullAddress: string) => {
    if (!fullAddress) return

    // Tenta extrair CEP do endereco (formato: 00000-000 ou 00000000)
    const cepMatch = fullAddress.match(/(\d{5}-?\d{3})/)
    if (cepMatch) {
      setEditCep(cepMatch[1])
    }

    // Tenta fazer parse do endereco
    // Formato esperado: "Rua, Numero, Bairro, Cidade - UF, CEP"
    const parts = fullAddress.split(',').map(p => p.trim())
    if (parts.length >= 1) setEditStreet(parts[0] || '')
    if (parts.length >= 2) {
      // Pode ser numero ou bairro
      const part = parts[1]
      if (/^\d+/.test(part)) {
        setEditNumber(part)
      } else {
        setEditNeighborhood(part)
      }
    }
    if (parts.length >= 3) setEditNeighborhood(parts[2] || '')
    if (parts.length >= 4) {
      // Cidade - UF
      const cityState = parts[3].split('-').map(p => p.trim())
      setEditCity(cityState[0] || '')
      if (cityState[1]) setEditState(cityState[1])
    }
  }

  // Montar endereco completo para salvar
  const buildFullAddress = () => {
    const parts = []
    if (editStreet) parts.push(editStreet)
    if (editNumber) parts.push(editNumber)
    if (editComplement) parts.push(editComplement)
    if (editNeighborhood) parts.push(editNeighborhood)
    if (editCity && editState) {
      parts.push(`${editCity} - ${editState}`)
    } else if (editCity) {
      parts.push(editCity)
    }
    if (editCep) parts.push(editCep)
    return parts.join(', ')
  }

  // Carregar perfil real do backend
  const fetchProfile = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)

      // Buscar perfil, branding e notificacoes em paralelo
      const [profileData, brandingData, notifSettings] = await Promise.all([
        partnerAdminApi.dashboard.getProfile(),
        whitelabelApi.getBranding().catch(() => null), // Se falhar, continua sem branding
        partnerAdminApi.dashboard.getNotificationSettings().catch(() => DEFAULT_NOTIFICATION_SETTINGS)
      ])

      setProfile(profileData)
      setBranding(brandingData)
      setNotificationSettings(notifSettings)
      setEditName(profileData.name)
      setEditContactEmail(profileData.contactEmail || '')
      setEditContactPhone(profileData.contactPhone || '')

      // Parse do endereco existente
      const existingAddress = brandingData?.contact?.address || ''
      parseAddress(existingAddress)
    } catch (err) {
      console.error('Error fetching profile:', err)
      setError('Erro ao carregar perfil do parceiro')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchProfile()
  }, [fetchProfile])

  // Salvar perfil real no backend
  const handleSaveProfile = async () => {
    if (!profile) return

    try {
      setSaving(true)
      setError(null)

      // Atualizar perfil basico
      const updatedProfile = await partnerAdminApi.dashboard.updateProfile({
        name: editName,
        contactEmail: editContactEmail,
        contactPhone: editContactPhone
      })

      // Atualizar endereco no branding
      const fullAddress = buildFullAddress()
      if (branding && fullAddress !== (branding.contact?.address || '')) {
        const updatedBranding = await whitelabelApi.updateBranding({
          contact: {
            ...branding.contact,
            address: fullAddress
          }
        })
        setBranding(updatedBranding)
      }

      setProfile(updatedProfile)
      setSuccess('Perfil atualizado com sucesso!')
      setTimeout(() => setSuccess(null), 3000)
    } catch (err) {
      console.error('Error saving profile:', err)
      setError('Erro ao salvar perfil')
    } finally {
      setSaving(false)
    }
  }

  // Mock save para funcionalidades nao implementadas
  const handleMockSave = async (section: string) => {
    setSaving(true)
    await new Promise(resolve => setTimeout(resolve, 1000))
    setSaving(false)
    setSuccess(`${section}: Esta funcionalidade sera implementada em breve`)
    setTimeout(() => setSuccess(null), 3000)
  }

  // Salvar configuracoes de notificacao - REAL
  const handleSaveNotifications = async () => {
    try {
      setSaving(true)
      setError(null)

      const updatedSettings = await partnerAdminApi.dashboard.updateNotificationSettings(notificationSettings)
      setNotificationSettings(updatedSettings)
      setSuccess('Configuracoes de notificacao salvas com sucesso!')
      setTimeout(() => setSuccess(null), 3000)
    } catch (err) {
      console.error('Error saving notification settings:', err)
      setError('Erro ao salvar configuracoes de notificacao')
    } finally {
      setSaving(false)
    }
  }

  // Toggle para tipos de notificacao
  const handleNotificationTypeToggle = (type: keyof NotificationSettings['notifications']) => {
    setNotificationSettings({
      ...notificationSettings,
      notifications: {
        ...notificationSettings.notifications,
        [type]: !notificationSettings.notifications[type]
      }
    })
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Configuracoes</h1>
        <p className="text-gray-600 mt-1">
          Gerencie as configuracoes do seu escritorio contabil
        </p>
      </div>

      {/* Alerts */}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 flex items-center gap-2">
          <AlertTriangle size={20} />
          {error}
          <button onClick={() => setError(null)} className="ml-auto">
            <X size={16} />
          </button>
        </div>
      )}
      {success && (
        <div className="p-4 bg-green-50 border border-green-200 rounded-lg text-green-700 flex items-center gap-2">
          <Check size={20} />
          {success}
          <button onClick={() => setSuccess(null)} className="ml-auto">
            <X size={16} />
          </button>
        </div>
      )}

      {/* Navigation Tabs */}
      <div className="bg-white rounded-xl border border-gray-200">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6">
            {[
              { id: 'profile', label: 'Perfil', icon: Building, implemented: true },
              { id: 'branding', label: 'Marca', icon: Palette, implemented: true, redirect: 'Whitelabel' },
              { id: 'notifications', label: 'Notificacoes', icon: Bell, implemented: true },
              { id: 'security', label: 'Seguranca', icon: Shield, implemented: true },
              { id: 'billing', label: 'Faturamento', icon: CreditCard, implemented: false }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center gap-2">
                  <tab.icon className="w-4 h-4" />
                  {tab.label}
                  {!tab.implemented && <AlertCircle className="w-3 h-3 text-red-500" />}
                  {tab.redirect && <ExternalLink className="w-3 h-3 text-blue-500" />}
                </div>
              </button>
            ))}
          </nav>
        </div>

        <div className="p-6">
          {/* Profile Tab - REAL DATA */}
          {activeTab === 'profile' && profile && (
            <div className="space-y-6">
              <div className="flex items-center gap-2 mb-4">
                <Check className="w-5 h-5 text-green-600" />
                <span className="text-sm text-green-600 font-medium">Dados reais do banco de dados</span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nome do Escritorio
                  </label>
                  <input
                    type="text"
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Slug (identificador unico)
                  </label>
                  <input
                    type="text"
                    value={profile.slug}
                    disabled
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    O slug nao pode ser alterado apos a criacao
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email de Contato
                  </label>
                  <input
                    type="email"
                    value={editContactEmail}
                    onChange={(e) => setEditContactEmail(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Telefone de Contato
                  </label>
                  <input
                    type="tel"
                    value={editContactPhone}
                    onChange={(e) => setEditContactPhone(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>

              {/* Info do Parceiro */}
              <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                <h4 className="font-medium text-gray-900 mb-3">Informacoes do Parceiro</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500">Status:</span>
                    <span className={`ml-2 px-2 py-0.5 rounded-full text-xs ${profile.active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                      {profile.active ? 'Ativo' : 'Inativo'}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-500">Comissao:</span>
                    <span className="ml-2 font-medium">{profile.commissionPercentage}%</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Criado em:</span>
                    <span className="ml-2">{new Date(profile.createdAt).toLocaleDateString('pt-BR')}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Atualizado:</span>
                    <span className="ml-2">{new Date(profile.updatedAt).toLocaleDateString('pt-BR')}</span>
                  </div>
                </div>
              </div>

              {/* Endereco */}
              <div className="mt-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  Endereco
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
                  {/* CEP */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      CEP
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        value={editCep}
                        onChange={(e) => {
                          const value = e.target.value.replace(/\D/g, '').slice(0, 8)
                          const formatted = value.length > 5 ? `${value.slice(0, 5)}-${value.slice(5)}` : value
                          setEditCep(formatted)
                          if (value.length === 8) {
                            fetchAddressByCep(value)
                          }
                        }}
                        placeholder="00000-000"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                      {loadingCep && (
                        <div className="absolute right-3 top-1/2 -translate-y-1/2">
                          <RefreshCw className="w-4 h-4 animate-spin text-blue-500" />
                        </div>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Digite o CEP para buscar automaticamente
                    </p>
                  </div>

                  {/* Rua */}
                  <div className="md:col-span-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Rua / Logradouro
                    </label>
                    <input
                      type="text"
                      value={editStreet}
                      onChange={(e) => setEditStreet(e.target.value)}
                      placeholder="Rua, Avenida, etc."
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  {/* Numero */}
                  <div className="md:col-span-1">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Numero
                    </label>
                    <input
                      type="text"
                      value={editNumber}
                      onChange={(e) => setEditNumber(e.target.value)}
                      placeholder="123"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  {/* Complemento */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Complemento
                    </label>
                    <input
                      type="text"
                      value={editComplement}
                      onChange={(e) => setEditComplement(e.target.value)}
                      placeholder="Sala 101, Bloco A"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  {/* Bairro */}
                  <div className="md:col-span-3">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Bairro
                    </label>
                    <input
                      type="text"
                      value={editNeighborhood}
                      onChange={(e) => setEditNeighborhood(e.target.value)}
                      placeholder="Bairro"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  {/* Cidade */}
                  <div className="md:col-span-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Cidade
                    </label>
                    <input
                      type="text"
                      value={editCity}
                      onChange={(e) => setEditCity(e.target.value)}
                      placeholder="Cidade"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  {/* Estado */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Estado
                    </label>
                    <input
                      type="text"
                      value={editState}
                      onChange={(e) => setEditState(e.target.value.toUpperCase().slice(0, 2))}
                      placeholder="UF"
                      maxLength={2}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>
              </div>

              <div className="flex justify-end">
                <button
                  onClick={handleSaveProfile}
                  disabled={saving}
                  className="flex items-center gap-2 px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {saving ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4" />
                  )}
                  {saving ? 'Salvando...' : 'Salvar Alteracoes'}
                </button>
              </div>
            </div>
          )}

          {/* Branding Tab - Redireciona para Whitelabel */}
          {activeTab === 'branding' && (
            <div className="space-y-6">
              <div className="p-6 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-blue-100 rounded-lg">
                    <Palette className="w-6 h-6 text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-blue-900">Personalizacao da Marca</h3>
                    <p className="text-blue-700 mt-1">
                      As configuracoes de marca foram movidas para uma pagina dedicada com mais opcoes de personalizacao.
                    </p>
                    <a
                      href="/whitelabel"
                      className="inline-flex items-center gap-2 mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      <ExternalLink className="w-4 h-4" />
                      Ir para Whitelabel
                    </a>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-4 border border-gray-200 rounded-lg">
                  <h4 className="font-medium text-gray-900 mb-2">Disponivel no Whitelabel:</h4>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-green-600" /> Cores personalizadas
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-green-600" /> Logo e Favicon
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-green-600" /> Tipografia
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-green-600" /> Dominio personalizado
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-green-600" /> Templates prontos
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-green-600" /> Configuracoes de email
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-green-600" /> SEO e redes sociais
                    </li>
                  </ul>
                </div>

                {profile?.brandingConfig && (
                  <div className="p-4 border border-gray-200 rounded-lg">
                    <h4 className="font-medium text-gray-900 mb-2">Configuracao Atual:</h4>
                    <div className="space-y-2 text-sm">
                      {profile.brandingConfig.primary_color && (
                        <div className="flex items-center gap-2">
                          <div
                            className="w-4 h-4 rounded border"
                            style={{ backgroundColor: profile.brandingConfig.primary_color }}
                          />
                          <span className="text-gray-600">Cor primaria: {profile.brandingConfig.primary_color}</span>
                        </div>
                      )}
                      {profile.brandingConfig.secondary_color && (
                        <div className="flex items-center gap-2">
                          <div
                            className="w-4 h-4 rounded border"
                            style={{ backgroundColor: profile.brandingConfig.secondary_color }}
                          />
                          <span className="text-gray-600">Cor secundaria: {profile.brandingConfig.secondary_color}</span>
                        </div>
                      )}
                      {profile.brandingConfig.support_email && (
                        <div className="text-gray-600">
                          Email suporte: {profile.brandingConfig.support_email}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Notifications Tab - Preferencias salvas, envio em breve */}
          {activeTab === 'notifications' && (
            <div className="space-y-6">
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg flex items-start gap-3">
                <Info className="w-5 h-5 text-blue-600 mt-0.5" />
                <div>
                  <h4 className="font-semibold text-blue-900">Preferencias salvas no sistema</h4>
                  <p className="text-sm text-blue-700 mt-1">
                    Suas preferencias de notificacao sao salvas no banco de dados. O envio de notificacoes por email sera ativado em breve.
                  </p>
                </div>
              </div>

              {/* Notification Methods */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  Metodos de Notificacao
                </h3>
                <div className="space-y-4">
                  {/* Email - Funcional em breve */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Mail className="w-5 h-5 text-blue-600" />
                      <div>
                        <p className="font-medium text-gray-900 flex items-center gap-2">
                          Email
                          <NotImplementedBadge tooltip="Envio de emails sera ativado em breve" />
                        </p>
                        <p className="text-sm text-gray-500">Receber notificacoes por email</p>
                      </div>
                    </div>
                    <button
                      onClick={() => setNotificationSettings({
                        ...notificationSettings,
                        email_notifications: !notificationSettings.email_notifications
                      })}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        notificationSettings.email_notifications ? 'bg-blue-600' : 'bg-gray-200'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${
                          notificationSettings.email_notifications ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>

                  {/* SMS - Nao implementado */}
                  <div className="flex items-center justify-between opacity-60">
                    <div className="flex items-center gap-3">
                      <Smartphone className="w-5 h-5 text-gray-400" />
                      <div>
                        <p className="font-medium text-gray-900 flex items-center gap-2">
                          SMS
                          <NotImplementedBadge tooltip="Integracao com SMS nao disponivel" />
                        </p>
                        <p className="text-sm text-gray-500">Receber notificacoes por SMS</p>
                      </div>
                    </div>
                    <button
                      disabled
                      className="relative inline-flex h-6 w-11 items-center rounded-full bg-gray-200 cursor-not-allowed"
                    >
                      <span className="inline-block h-4 w-4 transform rounded-full bg-white translate-x-1" />
                    </button>
                  </div>

                  {/* Push - Nao implementado */}
                  <div className="flex items-center justify-between opacity-60">
                    <div className="flex items-center gap-3">
                      <Monitor className="w-5 h-5 text-gray-400" />
                      <div>
                        <p className="font-medium text-gray-900 flex items-center gap-2">
                          Push
                          <NotImplementedBadge tooltip="Notificacoes push nao disponivel" />
                        </p>
                        <p className="text-sm text-gray-500">Notificacoes push no navegador</p>
                      </div>
                    </div>
                    <button
                      disabled
                      className="relative inline-flex h-6 w-11 items-center rounded-full bg-gray-200 cursor-not-allowed"
                    >
                      <span className="inline-block h-4 w-4 transform rounded-full bg-white translate-x-1" />
                    </button>
                  </div>
                </div>
              </div>

              {/* Notification Types - Preferencias salvas */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  Tipos de Notificacao
                  <span className="text-sm font-normal text-gray-500 ml-2">(preferencias salvas)</span>
                </h3>
                <div className="space-y-4">
                  {[
                    { key: 'tax_deadlines', label: 'Prazos Fiscais', desc: 'Lembretes de vencimento de obrigacoes' },
                    { key: 'document_uploads', label: 'Documentos Enviados', desc: 'Quando um cliente envia um documento' },
                    { key: 'banking_updates', label: 'Atualizacoes Bancarias', desc: 'Movimentacoes e pagamentos' },
                    { key: 'system_alerts', label: 'Alertas do Sistema', desc: 'Novidades e manutencoes programadas' },
                    { key: 'marketing', label: 'Marketing', desc: 'Novidades e promocoes' }
                  ].map((item) => (
                    <div key={item.key} className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-gray-900">{item.label}</p>
                        <p className="text-sm text-gray-500">{item.desc}</p>
                      </div>
                      <button
                        onClick={() => handleNotificationTypeToggle(item.key as keyof NotificationSettings['notifications'])}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                          notificationSettings.notifications[item.key as keyof NotificationSettings['notifications']]
                            ? 'bg-blue-600' : 'bg-gray-200'
                        }`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${
                            notificationSettings.notifications[item.key as keyof NotificationSettings['notifications']]
                              ? 'translate-x-6' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Frequency - Em breve */}
              <div className="opacity-60">
                <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                  Frequencia de Resumo
                  <NotImplementedBadge tooltip="Configuracao de frequencia sera implementada em breve" />
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                  {[
                    { value: 'realtime', label: 'Tempo Real' },
                    { value: 'hourly', label: 'Por Hora' },
                    { value: 'daily', label: 'Diario' },
                    { value: 'weekly', label: 'Semanal' },
                    { value: 'never', label: 'Nunca' }
                  ].map((option) => (
                    <button
                      key={option.value}
                      disabled
                      className={`px-4 py-2 rounded-lg border text-sm font-medium cursor-not-allowed ${
                        option.value === 'daily'
                          ? 'bg-gray-300 text-gray-600 border-gray-300'
                          : 'bg-white text-gray-400 border-gray-200'
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Quiet Hours - Em breve */}
              <div className="opacity-60">
                <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                  Horario de Silencio
                  <NotImplementedBadge tooltip="Horario de silencio sera implementado em breve" />
                </h3>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-gray-900">Ativar horario de silencio</p>
                    <p className="text-sm text-gray-500">Nao receber notificacoes durante o periodo configurado</p>
                  </div>
                  <button
                    disabled
                    className="relative inline-flex h-6 w-11 items-center rounded-full bg-gray-200 cursor-not-allowed"
                  >
                    <span className="inline-block h-4 w-4 transform rounded-full bg-white translate-x-1" />
                  </button>
                </div>
              </div>

              <div className="flex justify-end">
                <button
                  onClick={handleSaveNotifications}
                  disabled={saving}
                  className="flex items-center gap-2 px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {saving ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4" />
                  )}
                  {saving ? 'Salvando...' : 'Salvar Alteracoes'}
                </button>
              </div>
            </div>
          )}

          {/* Security Tab - NAO IMPLEMENTADO */}
          {activeTab === 'security' && (
            <SecurityTab />
          )}

          {/* Billing Tab - NAO IMPLEMENTADO */}
          {activeTab === 'billing' && (
            <div className="space-y-6">
              <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5" />
                <div>
                  <h4 className="font-semibold text-yellow-900">Funcionalidade em Desenvolvimento</h4>
                  <p className="text-sm text-yellow-700 mt-1">
                    O modulo de faturamento ainda nao esta implementado. Informacoes de pagamento e faturas estarao disponiveis em breve.
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 opacity-75">
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                    Informacoes de Faturamento
                    <NotImplementedBadge />
                  </h3>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-2">
                        Razao Social
                      </label>
                      <input
                        type="text"
                        disabled
                        placeholder="Nao implementado"
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-100 text-gray-400"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-2">
                        CNPJ
                      </label>
                      <input
                        type="text"
                        disabled
                        placeholder="Nao implementado"
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-100 text-gray-400"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-2">
                        Email para Faturamento
                      </label>
                      <input
                        type="email"
                        disabled
                        placeholder="Nao implementado"
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-100 text-gray-400"
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                    Plano Atual
                    <NotImplementedBadge />
                  </h3>

                  <div className="p-4 border border-gray-200 bg-gray-50 rounded-lg">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-lg font-semibold text-gray-500">
                        Plano nao definido
                      </span>
                      <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-200 text-gray-500">
                        -
                      </span>
                    </div>

                    {profile && (
                      <div className="space-y-2 text-sm text-gray-500">
                        <div className="flex justify-between">
                          <span>Comissao:</span>
                          <span>{profile.commissionPercentage}%</span>
                        </div>
                      </div>
                    )}

                    <button disabled className="w-full mt-4 px-4 py-2 bg-gray-300 text-gray-500 rounded-lg cursor-not-allowed">
                      Alterar Plano
                    </button>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                  Historico de Faturas
                  <NotImplementedBadge />
                </h3>
                <div className="p-8 text-center border border-gray-200 rounded-lg bg-gray-50">
                  <CreditCard className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">Nenhuma fatura disponivel</p>
                  <p className="text-sm text-gray-400 mt-1">O historico de faturas sera exibido aqui quando o modulo estiver implementado</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
